import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { CatalogItemOwnership, CatalogSuggestionSource } from 'fym-shared/src/smartlinks/catalog';
import { CatalogSuggestion } from '../../../../redux/api/fymAPI.types';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { buildProfileClaimSnapshot } from '../buildClaimSnapshot';
import { mergeMessages } from '../merge/mergeMessages';
import { useClaimRelease } from '../useClaimRelease';
import { claimWayMessages } from './claimWayMessages';

interface UseMergeSuggestionProps {
  accountId: number;
  /** Opens the claim dialog on the suggestion (design catalog-merge c3). */
  openDialog: (suggestion: CatalogSuggestion) => void;
}

/**
 * "Merge" on a suggestion with a match (design catalog-merge c1). A profile release carries its
 * links, so it merges at once. A catalog track must be read from musicapi first, so its merge
 * opens the claim dialog on the match (c3).
 */
export function useMergeSuggestion({ accountId, openDialog }: UseMergeSuggestionProps) {
  const intl = useIntl();
  const { claim } = useClaimRelease({ accountId });
  const { showSuccess, showError } = useShowToast();
  const [busyMaid, setBusyMaid] = useState<string>();

  const merge = useCallback(
    (suggestion: CatalogSuggestion) => {
      const match = suggestion.matchesCatalogItem;
      if (match === null || match === undefined) {
        return;
      }
      if (suggestion.source !== CatalogSuggestionSource.profile) {
        openDialog(suggestion);
        return;
      }
      setBusyMaid(suggestion.catalogTrackMaid);
      claim(buildProfileClaimSnapshot(suggestion), CatalogItemOwnership.mine, undefined, undefined, match.id)
        .then((claimed) => {
          if (claimed === null) {
            showError(intl.formatMessage(claimWayMessages.mergeFailed));
            return;
          }
          showSuccess(intl.formatMessage(mergeMessages.done, { from: suggestion.title, title: claimed.item.title }));
        })
        .catch(console.error)
        .finally(() => setBusyMaid(undefined));
    },
    [claim, intl, openDialog, showError, showSuccess]
  );

  return { merge, busyMaid };
}
